import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Check, Zap, CalendarCheck, ArrowRight } from "lucide-react";

const UpgradeModal = ({ isOpen, onClose, currentTier, limitType }) => {
  const messages = {
    search: {
      title: "Search Limit Reached",
      desc: "You've used all your weekly searches. Upgrade to Pro for unlimited property searches.",
    },
    analysis: {
      title: "You've Hit Your Analysis Limit",
      desc: "Upgrade to Pro to analyze unlimited properties and never miss a deal.",
    },
    alert: {
      title: "Alert Limit Reached",
      desc: "Free plan allows 5 alerts. Upgrade to Pro for unlimited instant alerts.",
    },
    saved_search: {
      title: "Saved Search Limit",
      desc: "Free plan allows 1 saved search. Upgrade to Pro for unlimited saved searches with email notifications.",
    },
    export: {
      title: "Export is a Pro Feature",
      desc: "Upgrade to Pro to download property analyses as PDF or Excel, and share deals via link.",
    },
    properties: {
      title: "Viewing Limit Reached",
      desc: "Upgrade to see all properties in your search results.",
    },
    top_deals: {
      title: "Top Deals — Pro Feature",
      desc: "Unlock full access to today's highest-ranked investment properties.",
    },
  };
  const msg = messages[limitType] || messages.properties;
  const isPro = currentTier === "pro_monthly" || currentTier === "pro_yearly";

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="max-w-lg bg-card border-border"
        data-testid="upgrade-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">{msg.title}</DialogTitle>
          <DialogDescription>{msg.desc}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-4">
          {!isPro && (
            <div className="p-4 rounded-xl border-2 border-emerald-500 bg-emerald-500/5">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-emerald-500" />
                  <span className="font-bold text-lg">Pro Monthly</span>
                </div>
                <span className="font-mono text-xl font-bold">
                  $19
                  <span className="text-sm text-muted-foreground font-normal">
                    /mo
                  </span>
                </span>
              </div>
              <ul className="text-sm text-muted-foreground space-y-1 mb-3">
                <li className="flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5 text-emerald-500" /> Unlimited
                  searches & analyses
                </li>
                <li className="flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5 text-emerald-500" /> All
                  results visible
                </li>
                <li className="flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5 text-emerald-500" /> Full Top
                  Deals access
                </li>
                <li className="flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5 text-emerald-500" /> Unlimited
                  alerts + Smart Sort
                </li>
                <li className="flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5 text-emerald-500" /> PDF &
                  Excel export
                </li>
              </ul>
              <Button
                className="w-full bg-emerald-500 hover:bg-emerald-600"
                onClick={() => (window.location.href = "/billing")}
                data-testid="upgrade-pro-monthly-btn"
              >
                Get Pro Monthly <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
          {!isPro && (
            <div className="p-4 rounded-xl border-2 border-blue-500 bg-blue-500/5">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <CalendarCheck className="w-5 h-5 text-blue-500" />
                  <span className="font-bold text-lg">Pro Annual</span>
                  <span className="text-xs font-bold text-emerald-500 bg-emerald-500/10 px-2 py-0.5 rounded-full">
                    Best Deal
                  </span>
                </div>
                <span className="font-mono text-xl font-bold">
                  $190
                  <span className="text-sm text-muted-foreground font-normal">
                    /yr
                  </span>
                </span>
              </div>
              <ul className="text-sm text-muted-foreground space-y-1 mb-3">
                <li className="flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5 text-blue-500" /> Everything in
                  Pro Monthly
                </li>
                <li className="flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5 text-blue-500" /> Save $38 per
                  year — 2 months free ($15.83/mo)
                </li>
              </ul>
              <Button
                variant="outline"
                className="w-full border-blue-500/50 text-blue-500 hover:bg-blue-500/10"
                onClick={() => (window.location.href = "/billing")}
                data-testid="upgrade-pro-yearly-btn"
              >
                Get Pro Annual <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
          {isPro && (
            <p className="text-sm text-muted-foreground text-center py-4">
              You're already on a Pro plan. If you're seeing limits, your usage
              may need to reset. Contact support for help.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UpgradeModal;
